import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router";
import { IntlProvider, type AbstractIntlMessages } from "use-intl";
import { LoadingState } from "@/components/ui/loading-state";
import { loadMessages } from "@/i18n/load-messages";
import { detectPreferredLocale, isAppLocale, rememberLocale, type AppLocale } from "@/i18n/routing";

/**
 * The root of every page: validates the `/:locale` URL segment, loads that
 * locale's message catalog, and provides it to the whole tree via
 * use-intl's IntlProvider (the same `useTranslations` API the components
 * were written against). An unknown first segment (e.g. an unprefixed
 * `/register?ref=CODE` referral link) is treated as a path and redirected
 * under the visitor's preferred locale instead of 404ing.
 */
export function LocaleLayout() {
  const { locale: segment } = useParams();
  const location = useLocation();
  const locale = isAppLocale(segment) ? segment : null;
  const [catalog, setCatalog] = useState<{ locale: AppLocale; messages: AbstractIntlMessages } | null>(null);

  useEffect(() => {
    if (!locale) return;
    let cancelled = false;
    document.documentElement.lang = locale;
    rememberLocale(locale);
    void loadMessages(locale).then((messages) => {
      if (!cancelled) setCatalog({ locale, messages });
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  if (!locale) {
    const preferred = detectPreferredLocale();
    return <Navigate to={`/${preferred}${location.pathname}${location.search}${location.hash}`} replace />;
  }

  if (!catalog) return <LoadingState fullPage message="" />;

  return (
    <IntlProvider locale={catalog.locale} messages={catalog.messages} timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}>
      <Outlet />
    </IntlProvider>
  );
}

/** `/` → `/<preferred locale>`. */
export function RootRedirect() {
  const location = useLocation();
  return <Navigate to={`/${detectPreferredLocale()}${location.search}${location.hash}`} replace />;
}
